import { Button, Col, Form, Input, Modal, Popconfirm, Row } from 'antd';
import type { FC } from 'react';

import type { Picture } from 'app/components/commons/Uploader/Types';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { dayjs } from 'app/utils/dayjs';
import { getFileSizeInBytes } from 'app/utils/files';

import { PictureComponent } from '../PictureComponent/PictureComponent';

import './EditPictureModal.scss';

const RULES = [{ required: true, message: 'Required' }];

export const EditPictureModal: FC<Props> = ({
  picture,
  closeModal,
  savePicture,
  deletePicture,
  kind,
  removable = false,
}) => {
  const [form] = Form.useForm<PictureFormValues>();

  const checkChanges = ({ name, alt, credit }: PictureFormValues) => {
    if (name !== picture.name) {
      return true;
    }

    if (alt !== picture.alt) {
      return true;
    }

    if (credit !== picture.credit) {
      return true;
    }

    return false;
  };

  const renderFooter = (values: PictureFormValues) => {
    const hasChanged = checkChanges(values);

    return (
      <>
        {removable && (
          <Popconfirm
            title="Delete this picture?"
            onConfirm={deletePicture}
            okText="Yes"
            cancelText="No"
            key="delete"
          >
            <Button>Delete</Button>
          </Popconfirm>
        )}
        <Button
          key="save"
          type="primary"
          onClick={() => form.submit()}
          disabled={!hasChanged}
        >
          Save
        </Button>
      </>
    );
  };

  return (
    <FormLegacyRenderProp<PictureFormValues>
      form={form}
      onFinish={savePicture}
      initialValues={picture}
      layout="vertical"
    >
      {(values) => (
        <Modal
          width={800}
          visible
          title={`Edit ${kind ? `${kind} ` : ''}Media`}
          onCancel={closeModal}
          footer={renderFooter(values)}
          className="edit-modal"
        >
          <Row className="edit-picture-modal">
            <Col span={9}>
              <div className="thumbnail">
                <PictureComponent
                  pictureId={picture.pictureId}
                  alt={picture.alt}
                  width={265}
                />
              </div>
              <div className="infos">
                <div>{dayjs(picture.updatedAt).format('MMMM Do YYYY')}</div>
                <div>
                  {picture.dimensions.width}x{picture.dimensions.height}
                  {' • '}
                  {getFileSizeInBytes(picture.size)}
                </div>
              </div>
            </Col>
            <Col span={15}>
              <Form.Item label="Title" name="name" rules={RULES}>
                <Input />
              </Form.Item>
              <Form.Item label="Alternative text" name="alt">
                <Input />
              </Form.Item>
              <Form.Item label="Credit" name="credit">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Modal>
      )}
    </FormLegacyRenderProp>
  );
};

export type PictureFormValues = Pick<Picture, 'name' | 'alt' | 'credit'>;

type Props = {
  picture: Picture;
  closeModal: () => void;
  savePicture: (picture: PictureFormValues) => void;
  deletePicture: () => void;
  kind?: string;
  removable?: boolean;
};
